import api from '../services/api';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

const isHttps = window.location.protocol === 'https:';
const cookieOptions = isHttps ? { sameSite: 'None', secure: true } : {};

// Function to store tokens
const storeTokens = (access_token, refresh_token, access_token_expired_at, refresh_token_expired_at) => {
    const accessTokenExpiryDate = new Date(access_token_expired_at.replace('WIB', '').trim());
    const refreshTokenExpiryDate = new Date(refresh_token_expired_at.replace('WIB', '').trim());
    
    Cookies.set('access', access_token, { expires: accessTokenExpiryDate, ...cookieOptions });
    Cookies.set('refresh', refresh_token, { expires: refreshTokenExpiryDate, ...cookieOptions });
};

// Function to clear tokens
export const clearTokens = () => {
    Cookies.remove('access');
    Cookies.remove('refresh');
    Cookies.remove('access_expired');
    Cookies.remove('refresh_expired');
};

let refreshingTokenPromise = null;

// Function to update token
const refreshToken = async () => {
    if (refreshingTokenPromise) {
        return refreshingTokenPromise;
    };
    
    refreshingTokenPromise = (async () => {
        try {
            const refreshToken = Cookies.get('refresh');
            if (!refreshToken) {
                throw new Error('No refresh token available')
            };
            
            const response = await api.post('/auth/token', {
                refresh_token: refreshToken
            });
            
            const { access_token, refresh_token, access_token_expired_at, refresh_token_expired_at } = response.data.data;
            storeTokens(access_token, refresh_token, access_token_expired_at, refresh_token_expired_at);
            
            return access_token;
        } catch (error) {
            throw error;
        } finally {
            refreshingTokenPromise = null;
        };
    })();
    
    return refreshingTokenPromise;
};

const isAccessTokenExpired = () => {
    const accessToken = Cookies.get('access');
    if (!accessToken) {
        return true
    }; // Token does not exist, assume expired
    
    // Check if token is less than 5 minutes away
    const { exp } = jwtDecode(accessToken);
    return exp < Math.floor(Date.now() / 1000) + 300;
};

export const isAccessTokenValid = async () => {
    if (isAccessTokenExpired()) {
        return await refreshToken();
    };
    
    return Cookies.get('access');
};

// Login Function
export const login = async (identifier, password) => {
    try {
        const response = await api.post('/auth/login', { identifier, password });
        const { access_token, refresh_token, access_token_expired_at, refresh_token_expired_at } = response.data.data;
        storeTokens(access_token, refresh_token, access_token_expired_at, refresh_token_expired_at);
        
        const { id } = jwtDecode(access_token);
        const userData = await api.get(`/user/${id}`, {
            headers: {
            Authorization: `Bearer ${access_token}`
            },
        });
        
        return { user: userData.data.data };
    } catch (error) {
        throw error.response?.data || error.message;
    };
};

// Change Password Function
export const changePassword = async (data) => {
    try {
        const accessToken = await isAccessTokenValid();
        const { id } = jwtDecode(accessToken);
        const response = await api.put(`/user/${id}/change-password`, data);
        
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    };
};

// Reset Password Function
export const resetPassword = async (id) => {
    try {
        await isAccessTokenValid();
        const response = await api.put(`/user/${id}/reset-password`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    };
};

// Logout function
export const logout = async () => {
    try {
        const accessToken = Cookies.get('access');
        const refreshToken = Cookies.get('refresh');
        if (accessToken && refreshToken) {
            await api.post('/auth/logout', { access_token: accessToken, refresh_token: refreshToken });
        };
        
        clearTokens();
    } catch (error) {
        throw error.response?.data || error.message;
    };
};
